import React, { useEffect, useState } from 'react';
import { Box, Typography, Avatar, BoxProps } from '@mui/material';
import { BirthdayInfo } from 'framework/ui/BirthdayInfo';
import { useArtifactInfoService, CROP_SIZES } from 'domain/shared/artifact';
import { usePositionService, WorkingStatusChip } from 'domain/shared/employee';

interface PersonCardProps extends Omit<BoxProps, 'position'> {
  name: string;
  photoId?: string;
  photoUrl?: string;
  dateOfBirth?: string | Date;
  positionId?: string;
  primaryTechnology?: string;
  secondaryTechnology?: string;
  competencyLevel?: string;
  status?: string;
  profileUrl?: string;
  size?: 'small' | 'medium' | 'large';
  showBirthday?: boolean;
  showPosition?: boolean;
  showStatus?: boolean;
  showTeams?: boolean;
  teamsComponent?: React.ReactNode;
  onClick?: () => void;
}

const PersonCard: React.FC<PersonCardProps> = ({
  name,
  photoId,
  photoUrl,
  dateOfBirth,
  positionId,
  primaryTechnology,
  secondaryTechnology,
  competencyLevel,
  status,
  profileUrl,
  size = 'medium',
  showBirthday = true,
  showPosition = true,
  showStatus = true,
  showTeams = false,
  teamsComponent,
  onClick,
  sx,
  ...boxProps
}) => {
  const { findPositionById } = usePositionService();
  const [positionName, setPositionName] = useState<Nullable<string>>(null);

  useEffect(() => {
    if (positionId) {
      findPositionById(positionId)
        .then(
          (positionResult: {
            name: string;
            primaryTechnology?: string;
            secondaryTechnology?: string;
            competencyLevel?: string;
          }) => {
            let displayName = positionResult.name;

            const techParts = [];
            const usePrimaryTech = primaryTechnology || positionResult.primaryTechnology;
            const useSecondaryTech = secondaryTechnology || positionResult.secondaryTechnology;
            const useCompetencyLevel = competencyLevel || positionResult.competencyLevel;

            if (usePrimaryTech) {
              techParts.push(usePrimaryTech);
            }
            if (useSecondaryTech) {
              techParts.push(useSecondaryTech);
            }

            if (techParts.length > 0) {
              displayName += ` (${techParts.join(', ')})`;
            }

            if (useCompetencyLevel) {
              displayName += ` - ${useCompetencyLevel}`;
            }

            setPositionName(displayName);
          },
        )
        .catch(() => {
          setPositionName(null);
        });
    }
  }, [positionId, primaryTechnology, secondaryTechnology, competencyLevel, findPositionById]);
  const { getThumbnails } = useArtifactInfoService();
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  useEffect(() => {
    if (photoId) {
      getThumbnails(photoId)
        .then(thumbnails => {
          if (thumbnails[CROP_SIZES.SIZE_100]) {
            setThumbnailUrl(thumbnails[CROP_SIZES.SIZE_100]);
          }
        })
        .catch(() => {
          setThumbnailUrl(null);
        });
    }
    // getThumbnails is a new function on every render; depending on it refetched on each re-render.
  }, [photoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const getInitials = () => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
  };

  const getAvatarSize = () => {
    if (size === 'small') return 40;
    if (size === 'medium') return 80;
    return 80;
  };

  const getFontSize = () => {
    if (size === 'small') return '0.875rem';
    if (size === 'medium') return '1rem';
    return '1.25rem';
  };

  const getPositionVariant = () => {
    if (size === 'small') return 'caption';
    return 'body2';
  };

  const avatarSize = getAvatarSize();
  const initials = getInitials();
  const sizeSx = {
    small: { padding: '12px', gap: '12px' },
    medium: {},
    large: { padding: '20px', gap: '20px' },
  }[size];

  return (
    <Box
      onClick={onClick}
      {...boxProps}
      sx={[
        {
          display: 'flex',
          alignItems: 'flex-start',
          gap: '16px',
          paddingBottom: '12px',
          borderRadius: '12px',
          backgroundColor: 'background.paper',
          ...sizeSx,
          '@media (max-width: 768px)': {
            padding: '12px',
            gap: '12px',
            ...(size === 'large' && { padding: '16px', gap: '16px' }),
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Avatar
          src={thumbnailUrl || photoUrl}
          alt={name}
          sx={{
            width: avatarSize,
            height: avatarSize,
            fontSize: getFontSize(),
            borderRadius: '4px',
          }}
        >
          {initials}
        </Avatar>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            '@media (max-width: 768px)': { flexDirection: 'column', alignItems: 'flex-start', gap: '8px' },
          }}
        >
          <Typography
            component={profileUrl ? 'a' : 'span'}
            {...(profileUrl && {
              href: profileUrl,
              target: '_blank',
              rel: 'noopener noreferrer',
            })}
            variant="h6"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              wordBreak: 'break-word',
              minWidth: 0,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {name}
          </Typography>

          {showStatus && status && <WorkingStatusChip sx={{ ml: 1 }} />}
          {showTeams && teamsComponent && <Box>{teamsComponent}</Box>}
        </Box>

        {showPosition && positionName && (
          <Typography variant={getPositionVariant()} color="text.secondary" sx={{ fontWeight: 500, margin: 0 }}>
            {positionName}
          </Typography>
        )}

        {showBirthday && dateOfBirth && (
          <BirthdayInfo
            dateOfBirth={dateOfBirth}
            sx={{
              alignItems: 'center',
              flexWrap: 'wrap',
              '@media (max-width: 768px)': { flexDirection: 'column', alignItems: 'flex-start', gap: '4px' },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default PersonCard;
